import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

export type AnnexBadgeColor = 'green' | 'orange' | 'red';

@Component({
  selector: 'app-annex-badge',
  templateUrl: './annex-badge.component.html',
  styleUrls: ['./annex-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [DatePipe]
})
export class AnnexBadgeComponent implements OnChanges {
  @Input() annexCount: number = 0;
  @Input() lastSignedAnnexDate: Date | null | undefined = null;
  @Input() hasUnsignedAnnex: boolean = false;
  @Input() hasInconsistentAnnex: boolean = false;
  @Input() hasImportedAnnex: boolean = false;
  @Input() isAdmin: boolean = false;

  tooltipText: string = '';
  visible: boolean = false;
  badgeColor: AnnexBadgeColor = 'green';

  constructor(
    private translateService: TranslateService,
    private datePipe: DatePipe
  ) {}

  ngOnChanges(): void {
    this.visible = this.annexCount > 0;
    this.badgeColor = this.computeBadgeColor();
    this.tooltipText = this.buildTooltip();
  }

  private computeBadgeColor(): AnnexBadgeColor {
    if (this.isAdmin && this.hasInconsistentAnnex) {
      return 'red';
    }
    if (this.hasUnsignedAnnex) {
      return 'orange';
    }
    return 'green';
  }

  private buildTooltip(): string {
    if (this.annexCount === 0) {
      return '';
    }

    const lines: string[] = [
      this.translateService.instant('AnnexBadge.AnnexCount', { count: this.annexCount })
    ];

    if (this.lastSignedAnnexDate) {
      const formatted = this.datePipe.transform(this.lastSignedAnnexDate, 'dd.MM.yyyy') ?? '';
      lines.push(this.translateService.instant('AnnexBadge.LastSignedAnnex', { date: formatted }));
    }

    if (this.hasUnsignedAnnex) {
      lines.push(this.translateService.instant('AnnexBadge.HasUnsignedAnnex'));
    }

    if (this.isAdmin && this.hasInconsistentAnnex) {
      lines.push(this.translateService.instant('AnnexBadge.HasInconsistentAnnex'));
    }

    if (this.hasImportedAnnex) {
      lines.push(this.translateService.instant('AnnexPanel.Imported'));
    }

    return lines.join('\n');
  }
}
